export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  contentHtml: string;
  category: string;
  imageUrl: string;
  author: string;
  publishedDate: string;
  readTime: string;
  tags: string[];
}

export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
  imageUrl: string;
}

export const categories: Category[] = [
  {
    slug: 'anatomy',
    name: '解剖结构',
    description: '深入了解女性生殖系统各器官的位置、形态与组织结构',
    icon: 'ri-heart-pulse-line',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20elegant%20medical%20illustration%20of%20female%20reproductive%20system%20anatomy%2C%20minimalist%20line%20art%20style%20with%20warm%20peach%20and%20cream%20tones%2C%20scientific%20educational%20poster%20aesthetic%2C%20clean%20delicate%20illustration%20on%20light%20background%2C%20professional%20medical%20artwork&width=800&height=500&seq=cat-anatomy&orientation=landscape',
  },
  {
    slug: 'physiology',
    name: '生理功能',
    description: '解析月经周期、激素调节及生殖系统各项生理机制',
    icon: 'ri-temp-hot-line',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20menstrual%20cycle%20phases%20shown%20as%20a%20circular%20diagram%2C%20soft%20feminine%20color%20palette%20with%20rose%20and%20cream%20tones%2C%20educational%20medical%20art%20style%2C%20clean%20white%20background%2C%20delicate%20watercolor%20texture&width=800&height=500&seq=cat-physiology&orientation=landscape',
  },
  {
    slug: 'diseases',
    name: '常见疾病',
    description: '认识妇科常见疾病的症状、成因及预防治疗方法',
    icon: 'ri-stethoscope-line',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20medical%20illustration%20of%20wellness%20and%20prevention%20concept%2C%20minimalist%20style%20with%20warm%20peach%20and%20beige%20tones%2C%20abstract%20floral%20elements%20with%20medical%20cross%20symbol%2C%20gentle%20feminine%20aesthetic%2C%20clean%20composition%20on%20light%20background&width=800&height=500&seq=cat-diseases&orientation=landscape',
  },
  {
    slug: 'wellness',
    name: '健康保养',
    description: '日常生殖健康护理、饮食调理与生活方式建议',
    icon: 'ri-plant-line',
    imageUrl: 'https://readdy.ai/api/search-image?query=Calm%20minimalist%20lifestyle%20illustration%20with%20natural%20elements%20herbs%20and%20wellness%20concept%2C%20soft%20sage%20green%20and%20cream%20color%20palette%2C%20gentle%20feminine%20aesthetic%2C%20clean%20white%20background%2C%20delicate%20botanical%20illustration%20style&width=800&height=500&seq=cat-wellness&orientation=landscape',
  },
  {
    slug: 'examination',
    name: '检查指南',
    description: '妇科检查项目介绍、体检频率建议与检查注意事项',
    icon: 'ri-calendar-check-line',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20medical%20checkup%20illustration%20with%20calendar%20and%20health%20icons%2C%20soft%20warm%20beige%20and%20cream%20tones%2C%20professional%20healthcare%20aesthetic%2C%20clean%20composition%20on%20light%20background%2C%20delicate%20line%20art%20style&width=800&height=500&seq=cat-examination&orientation=landscape',
  },
];

export const articles: Article[] = [
  {
    slug: 'ovary-structure-function',
    title: '卵巢的解剖结构与生理功能详解',
    excerpt: '卵巢是女性生殖系统的核心器官，承担着产生卵子和分泌性激素的双重功能。本文详细解析卵巢的位置、结构层次及其在内分泌系统中的关键作用。',
    contentHtml: `<p>卵巢是女性生殖系统中最重要的器官之一，位于盆腔内子宫两侧，左右各一，呈扁椭圆形。成年女性的卵巢大小约为4cm×3cm×1cm，表面呈灰白色。</p>
<h2>卵巢的解剖位置</h2>
<p>卵巢位于盆腔侧壁的卵巢窝内，由卵巢悬韧带和卵巢固有韧带固定。卵巢悬韧带将卵巢连接至盆腔侧壁，其中包含卵巢血管和神经；卵巢固有韧带则将卵巢内侧端与子宫角相连。</p>
<h2>卵巢的组织结构</h2>
<p>卵巢从外到内可分为以下层次：</p>
<ul><li><strong>表面上皮</strong>：单层扁平或立方上皮，覆盖卵巢表面</li><li><strong>白膜</strong>：致密结缔组织构成的薄层，位于上皮下方</li><li><strong>皮质</strong>：含有不同发育阶段的卵泡，是卵巢的功能主体</li><li><strong>髓质</strong>：由疏松结缔组织构成，含有丰富的血管、淋巴管和神经</li></ul>
<h2>卵巢的生理功能</h2>
<p>卵巢具有两大核心生理功能：一是产生并排出成熟卵子，即生殖功能；二是分泌雌激素、孕激素及少量雄激素，即内分泌功能。雌激素主要由卵泡颗粒细胞分泌，促进女性第二性征发育和维持；孕激素主要由黄体分泌，为胚胎着床和妊娠维持创造条件。</p>
<h2>卵泡的发育过程</h2>
<p>女性出生时卵巢内约有100-200万个原始卵泡，青春期时减少至约30-40万个。每个月经周期中，通常只有一个优势卵泡发育成熟并排卵，女性一生中总共约排出400-500个成熟卵子。</p>
<p>卵泡发育经历原始卵泡、初级卵泡、次级卵泡和成熟卵泡四个阶段，整个过程受下丘脑-垂体-卵巢轴的精密调控。</p>`,
    category: 'anatomy',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20elegant%20anatomical%20illustration%20of%20ovary%20and%20fallopian%20tube%2C%20minimalist%20medical%20art%20style%20with%20warm%20peach%20and%20cream%20tones%2C%20delicate%20line%20drawing%2C%20educational%20scientific%20poster%20aesthetic%2C%20clean%20light%20background&width=1200&height=600&seq=article-ovary&orientation=landscape',
    author: '张医生',
    publishedDate: '2026-06-15',
    readTime: '8分钟',
    tags: ['卵巢', '解剖结构', '卵泡发育', '雌激素'],
  },
  {
    slug: 'uterus-anatomy-menstruation',
    title: '子宫结构解析与月经周期机制',
    excerpt: '子宫是胚胎发育的场所，也是月经产生的器官。了解子宫的分层结构、位置变化以及月经周期的激素调控机制，对女性健康管理至关重要。',
    contentHtml: `<p>子宫是一个中空的肌性器官，位于盆腔中央，膀胱与直肠之间，呈倒置梨形。成年未孕子宫长约7-8cm，宽约4-5cm，厚约2-3cm。</p>
<h2>子宫的解剖分部</h2>
<p>子宫可分为子宫底、子宫体和子宫颈三部分。子宫底是两侧输卵管入口以上的部分；子宫体为中间膨大的部分；子宫颈是下端狭窄部分，突入阴道内。</p>
<h2>子宫壁的分层结构</h2>
<ul><li><strong>子宫内膜</strong>：最内层，随月经周期发生周期性变化，是胚胎着床的部位</li><li><strong>子宫肌层</strong>：中间层，由平滑肌构成，厚约1-2cm，妊娠时显著增厚</li><li><strong>子宫浆膜</strong>：最外层，为腹膜覆盖层</li></ul>
<h2>月经周期的阶段</h2>
<p>月经周期分为三个阶段：</p>
<p><strong>月经期（第1-5天）</strong>：子宫内膜脱落出血，雌激素和孕激素水平降至最低。</p>
<p><strong>增殖期（第6-14天）</strong>：在雌激素作用下，子宫内膜增生修复，卵泡发育成熟。</p>
<p><strong>分泌期（第15-28天）</strong>：排卵后黄体形成，分泌孕激素使内膜转化为分泌期内膜，为受精卵着床做准备。</p>
<h2>月经周期的激素调控</h2>
<p>月经周期受下丘脑-垂体-卵巢轴调控。下丘脑分泌GnRH刺激垂体分泌FSH和LH，FSH促进卵泡发育，LH峰触发排卵，卵巢分泌的雌孕激素反馈调节下丘脑和垂体的分泌活动。</p>`,
    category: 'anatomy',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20medical%20illustration%20of%20uterus%20anatomy%2C%20soft%20feminine%20color%20palette%20with%20rose%20and%20cream%20tones%2C%20delicate%20educational%20art%20style%2C%20clean%20white%20background%2C%20scientific%20poster%20aesthetic&width=1200&height=600&seq=article-uterus&orientation=landscape',
    author: '李医生',
    publishedDate: '2026-06-20',
    readTime: '10分钟',
    tags: ['子宫', '月经周期', '子宫内膜', '激素'],
  },
  {
    slug: 'fallopian-tube-function',
    title: '输卵管的精细结构与受精过程',
    excerpt: '输卵管是卵子与精子相遇的场所，也是受精卵向子宫运输的通道。其独特的解剖结构和生理功能是自然受孕的关键环节。',
    contentHtml: `<p>输卵管是一对细长的肌性管道，自子宫角向两侧延伸至卵巢附近，全长约10-14cm。每根输卵管可分为四个部分。</p>
<h2>输卵管的解剖分部</h2>
<ul><li><strong>子宫部</strong>：穿过子宫壁的部分，开口于子宫腔</li><li><strong>峡部</strong>：紧接子宫部的狭窄段，是输卵管结扎的常用部位</li><li><strong>壶腹部</strong>：管径最宽的部分，是受精发生的通常位置</li><li><strong>漏斗部</strong>：末端呈喇叭状扩大，边缘有输卵管伞，可捕捉排出的卵子</li></ul>
<h2>输卵管的组织结构</h2>
<p>输卵管壁由三层构成：黏膜层（内层，有纤毛上皮细胞和分泌细胞）、肌层（平滑肌，内环外纵排列）和浆膜层（外层腹膜）。</p>
<h2>受精过程</h2>
<p>排卵后，输卵管伞将卵子捕获送入输卵管壶腹部。若此时有精子存在，精子穿过卵子周围的放射冠和透明带，一个精子与卵子融合完成受精。受精卵在输卵管蠕动和纤毛摆动作用下，约3-4天后进入子宫腔。</p>
<h2>输卵管功能障碍</h2>
<p>输卵管堵塞或功能障碍是女性不孕的主要原因之一，约占不孕原因的25-35%。常见原因包括盆腔炎症、子宫内膜异位症和既往盆腹腔手术史。</p>`,
    category: 'anatomy',
    imageUrl: 'https://readdy.ai/api/search-image?query=Delicate%20anatomical%20illustration%20of%20fallopian%20tubes%20and%20fertilization%20concept%2C%20minimalist%20medical%20art%20with%20soft%20peach%20and%20cream%20tones%2C%20elegant%20scientific%20poster%20style%2C%20clean%20light%20background&width=1200&height=600&seq=article-tube&orientation=landscape',
    author: '王医生',
    publishedDate: '2026-07-02',
    readTime: '7分钟',
    tags: ['输卵管', '受精', '不孕', '解剖结构'],
  },
  {
    slug: 'hormone-regulation-cycle',
    title: '女性激素调控与生理周期全景解读',
    excerpt: '雌激素、孕激素、FSH、LH——这些激素如何协同调控月经周期？本文全景呈现下丘脑-垂体-卵巢轴的精密反馈机制。',
    contentHtml: `<p>女性生殖内分泌系统是一个精密的调控网络，以「下丘脑-垂体-卵巢轴」为核心，通过多种激素的相互作用维持月经周期的正常运行。</p>
<h2>下丘脑-垂体-卵巢轴</h2>
<p>下丘脑脉冲式分泌促性腺激素释放激素（GnRH），刺激腺垂体分泌卵泡刺激素（FSH）和黄体生成素（LH）。FSH和LH作用于卵巢，调控卵泡发育、排卵和黄体形成，卵巢分泌的雌孕激素又通过正负反馈调节下丘脑和垂体的分泌活动。</p>
<h2>五种关键激素</h2>
<ul><li><strong>GnRH</strong>：下丘脑分泌，调控FSH和LH的释放</li><li><strong>FSH</strong>：促进卵泡生长发育和成熟</li><li><strong>LH</strong>：触发排卵，促进黄体形成</li><li><strong>雌激素</strong>：由卵泡颗粒细胞分泌，促进内膜增殖</li><li><strong>孕激素</strong>：由黄体分泌，维持分泌期内膜</li></ul>
<h2>激素的周期性变化</h2>
<p>月经初期FSH轻度升高，促进卵泡发育。卵泡成熟时雌激素达峰值，触发LH峰引发排卵。排卵后黄体形成，孕激素显著升高。若未受孕，黄体退化，雌孕激素骤降导致月经来潮。</p>
<h2>激素失衡的常见表现</h2>
<p>激素失调可导致月经紊乱、排卵障碍、多囊卵巢综合征等问题。日常压力、作息不规律、过度节食和体重异常都可能干扰内分泌平衡。</p>`,
    category: 'physiology',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20hormone%20cycle%20and%20endocrine%20system%2C%20circular%20diagram%20design%20with%20soft%20rose%20and%20cream%20tones%2C%20feminine%20scientific%20art%20style%2C%20clean%20white%20background&width=1200&height=600&seq=article-hormone&orientation=landscape',
    author: '张医生',
    publishedDate: '2026-07-10',
    readTime: '9分钟',
    tags: ['激素', '内分泌', '月经周期', '下丘脑-垂体-卵巢轴'],
  },
  {
    slug: 'vagina-microbiome-health',
    title: '阴道微生态平衡与自洁功能',
    excerpt: '阴道拥有独特的微生态系统，乳酸杆菌维持的酸性环境是女性生殖健康的第一道防线。了解阴道自洁机制，科学认识阴道健康。',
    contentHtml: `<p>阴道是一个具有自洁能力的肌性管道，连接子宫与外阴，长约7-9cm。其独特的微生态环境是女性生殖健康的重要防线。</p>
<h2>阴道的解剖特征</h2>
<p>阴道前壁紧邻膀胱和尿道，后壁与直肠相邻。阴道壁由黏膜层、肌层和外膜三层构成。黏膜表面为复层扁平上皮，在雌激素影响下发生周期性变化。</p>
<h2>阴道微生态系统</h2>
<p>健康阴道的微生态以乳酸杆菌为主导菌群，它们将阴道上皮细胞内的糖原转化为乳酸，使阴道pH值维持在3.8-4.5的弱酸性环境。这种酸性环境能有效抑制致病菌的生长繁殖。</p>
<h2>阴道的自洁机制</h2>
<p>阴道具有重要的自洁功能。宫颈黏液、阴道上皮脱落细胞和正常菌群代谢产物共同形成阴道分泌物（白带），随重力作用自然排出体外。正常的白带呈白色或透明，无异味，量随月经周期变化。</p>
<h2>阴道微生态失衡</h2>
<p>当乳酸杆菌减少、pH值升高时，条件致病菌可能过度生长，导致细菌性阴道病、外阴阴道假丝酵母菌病等常见感染。过度清洁、滥用抗生素、不适当的阴道冲洗是微生态失衡的常见诱因。</p>`,
    category: 'anatomy',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20elegant%20medical%20illustration%20of%20female%20reproductive%20health%20and%20microbiome%20concept%2C%20minimalist%20style%20with%20warm%20peach%20and%20cream%20tones%2C%20delicate%20scientific%20art%2C%20clean%20light%20background&width=1200&height=600&seq=article-vagina&orientation=landscape',
    author: '刘医生',
    publishedDate: '2026-07-18',
    readTime: '8分钟',
    tags: ['阴道', '微生态', '乳酸杆菌', '白带'],
  },
  {
    slug: 'pcos-diagnosis-treatment',
    title: '多囊卵巢综合征（PCOS）的诊治指南',
    excerpt: '多囊卵巢综合征是育龄女性最常见的内分泌疾病之一，影响约5-10%的育龄女性。了解PCOS的诊断标准、临床表现与综合治疗方案。',
    contentHtml: `<p>多囊卵巢综合征（PCOS）是育龄女性最常见的内分泌代谢性疾病，患病率约为5-10%，是导致排卵障碍性不孕的主要原因。</p>
<h2>PCOS的诊断标准</h2>
<p>根据鹿特丹标准，满足以下三项中的两项即可诊断：</p>
<ul><li>稀发排卵或无排卵（月经稀发或闭经）</li><li>临床或生化高雄激素血症表现（多毛、痤疮、脱发等）</li><li>超声检查显示多囊卵巢形态（一侧或双侧卵巢有≥12个直径2-9mm的卵泡）</li></ul>
<h2>PCOS的临床表现</h2>
<p>常见表现包括月经不调或闭经、不孕、多毛症、痤疮、肥胖、黑棘皮症等。很多患者伴有胰岛素抵抗，增加2型糖尿病和心血管疾病风险。</p>
<h2>PCOS的综合治疗</h2>
<p><strong>生活方式干预</strong>：减重是超重/肥胖PCOS患者的一线治疗，减重5-10%即可显著改善排卵功能和代谢指标。</p>
<p><strong>药物治疗</strong>：口服避孕药调整月经周期，二甲双胍改善胰岛素抵抗，来曲唑或克罗米芬促排卵。</p>
<p><strong>长期管理</strong>：定期监测血糖、血脂、血压，预防远期代谢并发症。</p>`,
    category: 'diseases',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20minimalist%20medical%20illustration%20of%20hormonal%20health%20and%20ovarian%20wellness%20concept%2C%20warm%20peach%20and%20cream%20color%20palette%2C%20delicate%20feminine%20scientific%20art%20style%2C%20clean%20light%20background&width=1200&height=600&seq=article-pcos&orientation=landscape',
    author: '陈医生',
    publishedDate: '2026-07-25',
    readTime: '11分钟',
    tags: ['多囊卵巢综合征', 'PCOS', '不孕', '内分泌'],
  },
  {
    slug: 'endometriosis-guide',
    title: '子宫内膜异位症：从认识到管理',
    excerpt: '子宫内膜异位症影响着全球约10%的育龄女性，是导致痛经和盆腔疼痛的重要原因。深度了解其发病机制、诊断方法和治疗选择。',
    contentHtml: `<p>子宫内膜异位症（内异症）是指具有生长功能的子宫内膜组织出现在子宫腔以外的部位，是育龄女性常见的慢性妇科疾病。</p>
<h2>内异症的发病机制</h2>
<p>内异症的发病机制尚未完全阐明，目前被广泛接受的是经血逆流学说：月经期部分经血通过输卵管逆流入盆腔，内膜碎片在盆腔腹膜种植生长。此外，免疫因素、遗传因素、环境因素也在发病中起重要作用。</p>
<h2>内异症的临床表现</h2>
<ul><li><strong>痛经</strong>：继发性进行性加重的痛经，是内异症最典型的症状</li><li><strong>慢性盆腔痛</strong>：非经期的持续性下腹疼痛</li><li><strong>性交痛</strong>：深部性交时疼痛明显</li><li><strong>不孕</strong>：约30-50%的内异症患者合并不孕</li><li><strong>月经异常</strong>：经量增多、经期延长</li></ul>
<h2>诊断与治疗</h2>
<p>腹腔镜检查是诊断内异症的金标准。治疗策略根据患者年龄、症状严重程度、生育需求个体化制定，包括药物治疗（止痛药、激素类药物）和手术治疗（保守性手术或根治性手术）。</p>`,
    category: 'diseases',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20feminine%20medical%20illustration%20of%20pelvic%20health%20and%20wellness%20concept%2C%20minimalist%20style%20with%20warm%20rose%20and%20cream%20tones%2C%20delicate%20scientific%20art%2C%20clean%20light%20background&width=1200&height=600&seq=article-endo&orientation=landscape',
    author: '赵医生',
    publishedDate: '2026-08-01',
    readTime: '10分钟',
    tags: ['子宫内膜异位症', '痛经', '盆腔疼痛', '不孕'],
  },
  {
    slug: 'cervical-cancer-screening',
    title: '宫颈癌筛查全攻略：TCT与HPV检测',
    excerpt: '宫颈癌是可防可治的恶性肿瘤，定期筛查是预防的关键。本文详细介绍TCT和HPV检测的原理、流程及筛查频率建议。',
    contentHtml: `<p>宫颈癌是全球女性第四大常见恶性肿瘤，但也是唯一可通过筛查有效预防的妇科恶性肿瘤。规范化的宫颈癌筛查可显著降低宫颈癌的发病率和死亡率。</p>
<h2>宫颈癌的病因</h2>
<p>高危型人乳头瘤病毒（HPV）持续感染是宫颈癌发生的必要条件。HPV主要通过性接触传播，大多数女性一生中都会感染HPV，但90%以上会被免疫系统自然清除。只有高危型HPV持续感染才可能导致宫颈病变。</p>
<h2>主要筛查方法</h2>
<p><strong>TCT（液基薄层细胞学检测）</strong>：采集宫颈脱落细胞进行病理学检查，评估细胞形态是否异常。是目前最常用的筛查手段。</p>
<p><strong>HPV检测</strong>：检测宫颈分泌物中是否存在高危型HPV DNA。HPV检测敏感性高于TCT，两者联合筛查可进一步提高检出率。</p>
<h2>筛查建议</h2>
<ul><li>25-29岁：每3年一次TCT筛查</li><li>30-65岁：每3-5年一次TCT+HPV联合筛查</li><li>65岁以上：既往筛查结果正常可停止筛查</li></ul>
<h2>HPV疫苗</h2>
<p>HPV疫苗可预防最常见的高危型和低危型HPV感染，是宫颈癌一级预防措施。建议9-45岁女性接种，最佳接种年龄为9-14岁。</p>`,
    category: 'diseases',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20medical%20prevention%20illustration%20with%20shield%20and%20health%20symbols%2C%20soft%20sage%20green%20and%20cream%20tones%2C%20feminine%20scientific%20art%20style%2C%20clean%20white%20background&width=1200&height=600&seq=article-cervical&orientation=landscape',
    author: '孙医生',
    publishedDate: '2026-08-05',
    readTime: '9分钟',
    tags: ['宫颈癌', '筛查', 'TCT', 'HPV', '疫苗'],
  },
  {
    slug: 'menstrual-hygiene-care',
    title: '经期卫生与日常护理要点',
    excerpt: '科学的经期护理不仅关乎舒适感，更直接影响生殖健康。从卫生用品选择到饮食调理，掌握经期保健的核心要点。',
    contentHtml: `<p>月经是女性生殖系统健康的「晴雨表」，科学的经期护理对维护生殖健康至关重要。</p>
<h2>卫生用品的选择与使用</h2>
<p>卫生巾应选择纯棉表层、透气性好、不含香料的产品，每2-4小时更换一次。卫生棉条需根据经量选择合适吸收量，每4-8小时更换，最长不超过8小时。月经杯可重复使用，需每次取出清洗消毒。</p>
<h2>经期个人卫生</h2>
<p>经期应每日温水清洗外阴，从前向后擦拭。避免盆浴和阴道冲洗。勤换内裤，选择纯棉透气材质。</p>
<h2>经期饮食调理</h2>
<p>建议增加富含铁质的食物（红肉、动物肝脏、菠菜）补充失血。适量摄入温性食物（姜茶、红糖水）有助于缓解痛经。避免生冷寒凉食物、咖啡因和过度辛辣食物。</p>
<h2>经期运动建议</h2>
<p>适度运动如散步、瑜伽有助于缓解经期不适，但应避免剧烈运动、倒立和长时间站立。痛经严重时以休息为主。</p>`,
    category: 'wellness',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20minimalist%20lifestyle%20illustration%20of%20feminine%20self-care%20and%20wellness%2C%20warm%20peach%20and%20cream%20color%20palette%2C%20delicate%20botanical%20elements%2C%20clean%20aesthetic%20on%20light%20background&width=1200&height=600&seq=article-hygiene&orientation=landscape',
    author: '刘医生',
    publishedDate: '2026-08-08',
    readTime: '7分钟',
    tags: ['经期护理', '卫生用品', '饮食调理', '日常保健'],
  },
  {
    slug: 'fertility-preservation',
    title: '女性生育力保护与科学备孕',
    excerpt: '年龄是影响女性生育力的最重要因素。了解卵巢储备功能评估方法、生育力保护策略和科学备孕的核心步骤。',
    contentHtml: `<p>女性生育力随着年龄增长而自然下降，了解自己的生育力状况并采取科学的保护措施，对实现生育计划至关重要。</p>
<h2>年龄与生育力的关系</h2>
<p>女性生育力的黄金期在20-30岁之间，30岁后开始缓慢下降，35岁后下降加速，40岁后自然受孕率显著降低。这与卵巢内卵泡数量的自然消耗和卵子质量下降密切相关。</p>
<h2>卵巢储备功能评估</h2>
<ul><li><strong>AMH（抗缪勒管激素）</strong>：反映卵巢储备的可靠指标，不受月经周期影响</li><li><strong>窦卵泡计数（AFC）</strong>：阴道超声计数卵巢内直径2-10mm的卵泡数量</li><li><strong>FSH和E2</strong>：月经第2-4天检测，FSH升高提示卵巢储备下降</li></ul>
<h2>科学备孕要点</h2>
<p>备孕前3个月开始补充叶酸（每日400-800μg），预防胎儿神经管缺陷。保持健康体重，BMI控制在18.5-24之间。戒烟限酒，减少咖啡因摄入。合理安排性生活，排卵期前后每1-2天同房一次。</p>
<h2>生育力保存选择</h2>
<p>对于暂时无生育计划的女性，可考虑卵子冷冻（俗称冻卵）保存生育力。卵子质量与冷冻时的年龄直接相关，建议在35岁前完成。</p>`,
    category: 'physiology',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20fertility%20and%20pregnancy%20preparation%20concept%2C%20soft%20warm%20rose%20and%20cream%20tones%2C%20feminine%20scientific%20art%20style%2C%20clean%20light%20background&width=1200&height=600&seq=article-fertility&orientation=landscape',
    author: '陈医生',
    publishedDate: '2026-08-03',
    readTime: '10分钟',
    tags: ['生育力', '备孕', 'AMH', '卵子冷冻'],
  },
  {
    slug: 'gynecological-examination-guide',
    title: '妇科检查项目全解析与频率建议',
    excerpt: '定期妇科检查是女性生殖健康的重要保障。了解各项检查的目的、流程和推荐频率，消除对妇科检查的恐惧与误解。',
    contentHtml: `<p>定期妇科检查是维护女性生殖健康的基础。很多妇科疾病在早期没有明显症状，通过定期体检才能及早发现和治疗。</p>
<h2>基础妇科检查项目</h2>
<ul><li><strong>妇科内诊</strong>：医生通过视诊和触诊检查外阴、阴道、宫颈、子宫及附件情况</li><li><strong>阴道分泌物检查</strong>：检查清洁度、滴虫、霉菌等</li><li><strong>TCT（宫颈细胞学检查）</strong>：筛查宫颈病变和宫颈癌</li><li><strong>盆腔超声</strong>：检查子宫、卵巢的形态结构，发现肌瘤、囊肿等</li></ul>
<h2>进阶检查项目</h2>
<p><strong>HPV检测</strong>：检测宫颈高危型HPV感染。乳腺超声或钼靶：筛查乳腺疾病。性激素六项：评估卵巢功能和内分泌状态。肿瘤标志物（CA125等）：辅助诊断卵巢肿瘤。</p>
<h2>各年龄段筛查频率建议</h2>
<ul><li>20-30岁：每年一次妇科内诊+盆腔超声，每3年一次TCT</li><li>30-40岁：每年一次妇科内诊+盆腔超声，每3-5年一次TCT+HPV联合筛查</li><li>40-50岁：每年一次全面妇科体检，增加乳腺钼靶筛查</li><li>50岁以上：继续定期筛查，重点关注子宫内膜和卵巢</li></ul>
<h2>检查前注意事项</h2>
<p>避开月经期，最佳检查时间为月经干净后3-7天。检查前3天避免性生活、阴道用药和阴道冲洗。超声检查需根据类型决定是否需要憋尿。</p>`,
    category: 'examination',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20medical%20checkup%20illustration%20with%20checklist%20and%20health%20icons%2C%20soft%20sage%20green%20and%20cream%20tones%2C%20feminine%20healthcare%20aesthetic%2C%20clean%20light%20background&width=1200&height=600&seq=article-exam&orientation=landscape',
    author: '赵医生',
    publishedDate: '2026-08-06',
    readTime: '9分钟',
    tags: ['妇科检查', '体检', '筛查', '预防保健'],
  },
  {
    slug: 'pelvic-floor-health',
    title: '盆底肌功能锻炼与生殖健康',
    excerpt: '盆底肌群是支撑盆腔器官的关键结构，其功能状态直接影响排尿、排便和性生活质量。学习凯格尔运动，科学锻炼盆底肌。',
    contentHtml: `<p>盆底肌群由多层肌肉和筋膜组成，像一张「吊床」支撑着膀胱、子宫和直肠。盆底肌功能良好对维持女性盆腔器官正常位置和功能至关重要。</p>
<h2>盆底肌的解剖与功能</h2>
<p>盆底肌主要包括肛提肌群（耻骨直肠肌、耻骨尾骨肌和髂骨尾骨肌）和尾骨肌。它们承载盆腔器官重量、维持尿道和肛门括约功能，并在分娩时形成产道的一部分。</p>
<h2>盆底功能障碍</h2>
<p>盆底肌松弛可导致压力性尿失禁（咳嗽、跳跃时漏尿）、盆腔器官脱垂（子宫脱垂、阴道壁膨出）和性功能障碍。妊娠分娩、慢性便秘、长期负重和绝经是主要危险因素。</p>
<h2>凯格尔（Kegel）运动</h2>
<p>凯格尔运动是锻炼盆底肌最有效的方法。正确找到盆底肌：尝试排尿时突然中断尿流的肌肉就是盆底肌。收缩盆底肌5秒，放松5秒，重复10-15次为一组，每日进行3组训练。注意保持腹部、臀部和大腿放松。</p>
<h2>日常盆底保护</h2>
<p>避免长期便秘和用力排便，控制体重避免肥胖，产后及时进行盆底康复训练，避免长时间负重和剧烈跳跃运动。</p>`,
    category: 'wellness',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20minimalist%20wellness%20illustration%20of%20pelvic%20health%20and%20exercise%20concept%2C%20warm%20peach%20and%20cream%20tones%2C%20feminine%20fitness%20art%20style%2C%20clean%20light%20background&width=1200&height=600&seq=article-pelvic&orientation=landscape',
    author: '李医生',
    publishedDate: '2026-08-04',
    readTime: '8分钟',
    tags: ['盆底肌', '凯格尔运动', '尿失禁', '产后康复'],
  },
  {
    slug: 'menopause-health-management',
    title: '围绝经期健康管理与激素补充治疗',
    excerpt: '围绝经期是女性生命中的重要转折阶段。科学认识更年期变化，了解激素补充治疗的利弊，平稳度过这一特殊时期。',
    contentHtml: `<p>围绝经期（也称更年期）是女性卵巢功能逐渐衰退直至完全停止的过渡时期，通常发生在45-55岁之间，平均年龄约50岁。</p>
<h2>围绝经期的阶段分期</h2>
<p>围绝经期包括绝经过渡期（月经开始紊乱至最后一次月经）和绝经后期（最后一次月经后一年）。连续12个月无月经可确诊为绝经。</p>
<h2>围绝经期常见症状</h2>
<ul><li><strong>血管舒缩症状</strong>：潮热、盗汗，是最常见的症状</li><li><strong>精神神经症状</strong>：情绪波动、焦虑、抑郁、失眠</li><li><strong>泌尿生殖道萎缩</strong>：阴道干涩、性交痛、反复尿路感染</li><li><strong>远期健康影响</strong>：骨质疏松、心血管疾病风险增加</li></ul>
<h2>激素补充治疗（HRT）</h2>
<p>HRT是缓解围绝经期症状最有效的方法。在「窗口期」（60岁以下或绝经10年内）开始的HRT获益大于风险。HRT可有效缓解潮热盗汗、改善阴道萎缩、预防骨质疏松。</p>
<h2>非激素治疗与生活方式管理</h2>
<p>规律运动（每周至少150分钟中等强度运动）、均衡饮食（增加钙和维生素D摄入）、保持健康体重、戒烟限酒、进行盆底肌锻炼。植物雌激素（大豆异黄酮）对轻中度症状可能有一定帮助。</p>`,
    category: 'wellness',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20lifestyle%20illustration%20of%20mature%20women%20wellness%20and%20self-care%2C%20warm%20sage%20green%20and%20cream%20tones%2C%20feminine%20health%20art%20style%2C%20clean%20light%20background&width=1200&height=600&seq=article-menopause&orientation=landscape',
    author: '孙医生',
    publishedDate: '2026-08-02',
    readTime: '10分钟',
    tags: ['围绝经期', '更年期', '激素补充治疗', 'HRT'],
  },
  {
    slug: 'breast-self-examination',
    title: '乳腺自检方法与乳腺癌早期发现',
    excerpt: '乳腺健康是女性生殖健康的重要组成部分。掌握正确的乳腺自检方法，了解乳腺癌的早期信号和筛查策略。',
    contentHtml: `<p>乳腺作为女性重要的第二性征器官，其健康状况直接影响女性的身心健康。乳腺癌是全球女性发病率最高的恶性肿瘤，早期发现治愈率可达90%以上。</p>
<h2>乳腺自检的最佳时间</h2>
<p>绝经前女性建议在月经干净后3-7天进行，此时乳腺组织受激素影响最小，更容易发现异常。绝经后女性可固定在每月同一天进行自检。</p>
<h2>乳腺自检「三步法」</h2>
<p><strong>视诊</strong>：站在镜前，双手叉腰和举过头顶，观察双侧乳房是否对称、有无皮肤凹陷（橘皮样改变）、乳头有无内陷或溢液。</p>
<p><strong>触诊（站立位）</strong>：用对侧手指并拢以打圈方式由外向内触诊整个乳房和腋窝，注意有无肿块或增厚。</p>
<p><strong>触诊（仰卧位）</strong>：平躺后肩下垫薄枕，同侧手臂上举，用对侧手以同样的方式触诊乳房。</p>
<h2>需要警惕的信号</h2>
<p>无痛性乳房肿块、乳房皮肤凹陷或橘皮样改变、乳头溢液（尤其是血性溢液）、乳头内陷或湿疹样改变、腋窝淋巴结肿大。</p>`,
    category: 'wellness',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20feminine%20healthcare%20illustration%20of%20breast%20health%20awareness%20concept%2C%20minimalist%20style%20with%20warm%20rose%20and%20cream%20tones%2C%20delicate%20medical%20art%2C%20clean%20light%20background&width=1200&height=600&seq=article-breast&orientation=landscape',
    author: '刘医生',
    publishedDate: '2026-08-07',
    readTime: '7分钟',
    tags: ['乳腺', '自检', '乳腺癌', '筛查'],
  },
  {
    slug: 'sexual-health-education',
    title: '女性性健康与生殖系统保护',
    excerpt: '科学认识女性性反应周期、常见性健康问题及安全性行为知识。打破传统观念束缚，建立正确的性健康观念。',
    contentHtml: `<p>性健康是女性整体健康的重要组成部分，世界卫生组织将其定义为「与性有关的身体、情感、心理和社会层面的完好状态」。</p>
<h2>女性性反应周期</h2>
<p>女性性反应分为四个阶段：兴奋期（生殖器充血、阴道润滑）、平台期（阴道外1/3充血形成高潮平台）、高潮期（盆底肌有节律收缩）和消退期（逐渐恢复未兴奋状态）。女性通常需要比男性更长的兴奋时间。</p>
<h2>常见性健康问题</h2>
<ul><li><strong>性欲减退</strong>：可能由激素水平变化、心理因素、疲劳或药物影响导致</li><li><strong>性唤起障碍</strong>：阴道润滑不足，常与雌激素水平下降有关</li><li><strong>性交痛</strong>：可能由感染、子宫内膜异位症或盆底肌紧张导致</li></ul>
<h2>安全性行为</h2>
<p>正确和持续使用安全套是预防性传播感染的有效手段。定期进行性传播疾病筛查（HIV、梅毒、衣原体、淋病等）是保护生殖健康的重要措施。</p>
<h2>定期妇科体检的重要性</h2>
<p>即使没有症状，有性生活的女性也应每年进行一次妇科体检，包括TCT和HPV筛查。早期发现和治疗能显著改善预后。</p>`,
    category: 'examination',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20intimate%20health%20and%20wellness%20concept%2C%20soft%20warm%20peach%20and%20cream%20tones%2C%20feminine%20healthcare%20art%2C%20clean%20light%20background&width=1200&height=600&seq=article-sexual&orientation=landscape',
    author: '王医生',
    publishedDate: '2026-08-09',
    readTime: '8分钟',
    tags: ['性健康', '安全性行为', '性教育', '妇科体检'],
  },
];

export const getArticlesByCategory = (categorySlug: string): Article[] => {
  return articles.filter((a) => a.category === categorySlug);
};

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((a) => a.slug === slug);
};

export const getCategoryBySlug = (slug: string): Category | undefined => {
  return categories.find((c) => c.slug === slug);
};

export const faqItems = [
  {
    question: '月经周期多少天算正常？',
    answer: '正常月经周期为21-35天，平均28天。周期长度因人而异，只要规律且在正常范围内即为正常。偶尔的周期变化（如压力、旅行导致的延迟或提前）无需过度担忧。如果周期持续不规律（变动超过7天），建议咨询妇科医生评估是否存在内分泌失调。',
  },
  {
    question: '多大年龄开始做宫颈癌筛查？',
    answer: '根据国内外指南建议，25岁开始进行宫颈癌筛查。25-29岁可每3年做一次TCT（宫颈细胞学检查）；30-65岁建议每3-5年进行TCT和HPV联合筛查。已接种HPV疫苗的女性仍需按规定进行筛查，因为疫苗不能覆盖所有高危型HPV。',
  },
  {
    question: '痛经正常吗？什么时候需要就医？',
    answer: '原发性痛经（无器质性病变）在年轻女性中很常见，一般不需特殊治疗。但如果痛经逐渐加重、影响正常工作生活、止痛药效果不佳，或伴有月经量异常增多、非经期疼痛等症状，需及时就医排查子宫内膜异位症、子宫腺肌病等继发性痛经病因。',
  },
  {
    question: '白带什么样算正常？',
    answer: '正常白带呈白色或透明、无异味，量随月经周期变化——排卵期增多呈蛋清样拉丝状，黄体期变稠。若出现黄绿色、泡沫状、豆腐渣样白带，或伴有异味、外阴瘙痒灼痛等不适，提示可能有感染，应及时就诊。',
  },
  {
    question: '卵巢早衰可以预防吗？',
    answer: '卵巢早衰指40岁前卵巢功能衰竭，确切病因多不明确。虽然无法完全预防，但可通过以下方式保护卵巢功能：避免盆腔手术损伤卵巢血供、戒烟、保持健康体重、避免接触环境内分泌干扰物。有家族史的女性可考虑在适龄期完成生育计划。',
  },
  {
    question: 'HPV疫苗多大年龄接种有效？',
    answer: 'HPV疫苗推荐接种年龄为9-45岁，最佳接种年龄为9-14岁（首次性行为之前）。已有性生活的女性仍可从接种中获益，因为可能尚未感染疫苗覆盖的所有HPV型别。即使已感染过某种型别的HPV，疫苗仍能预防其他型别的感染。',
  },
];